import { useEffect, useRef } from "react";
import pedidos from "@/assets/pedidos.png";

/** Tuning knobs for the scroll-linked 3D rise. */
const MAX_TILT = 55; // degrees it lies back at the top
const MIN_SCALE = 0.9; // how small/far it starts
const MAX_LIFT = 40; // px it travels up as it stands
const RISE_FACTOR = 0.6; // fraction of viewport height to fully straighten

/** Ease-out so the motion decelerates as it settles upright. */
const easeOut = (t: number) => 1 - (1 - t) ** 3;

/**
 * Real product screenshot for the hero. It starts "lying down" — rotated back
 * in 3D, smaller and further away, hinged at its bottom edge — and rises to
 * upright as the visitor scrolls, growing forward with a ground shadow that
 * settles beneath it. Scroll-linked via requestAnimationFrame, no animation
 * library. Honors reduced-motion.
 */
export function ProductShot() {
  const tiltRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const card = tiltRef.current;
    const shadow = shadowRef.current;
    if (!card) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const update = () => {
      const distance = Math.max(1, window.innerHeight * RISE_FACTOR);
      const raw = Math.min(1, Math.max(0, window.scrollY / distance));
      const p = easeOut(raw);

      const tilt = (1 - p) * MAX_TILT;
      const scale = MIN_SCALE + (1 - MIN_SCALE) * p;
      const lift = (1 - p) * MAX_LIFT;
      card.style.transform = `translateY(${lift}px) scale(${scale}) rotateX(${tilt}deg)`;

      if (shadow) {
        shadow.style.opacity = `${0.08 + 0.24 * p}`;
        shadow.style.transform = `scaleX(${0.6 + 0.4 * p}) scaleY(${0.5 + 0.5 * p})`;
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="relative" style={{ perspective: "1000px", perspectiveOrigin: "50% 0%" }}>
      {/* Ground shadow — settles beneath the card as it stands up. */}
      <div
        ref={shadowRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-10 -bottom-8 -z-10 h-16 rounded-[50%] bg-black/40 blur-2xl"
        style={{ opacity: 0.08 }}
      />
      <div
        ref={tiltRef}
        className="overflow-hidden rounded-2xl border border-[var(--separator)] bg-[var(--surface)] shadow-2xl shadow-black/10 will-change-transform"
        style={{ transformOrigin: "center bottom" }}
      >
        <img
          src={pedidos}
          alt="Tablero de pedidos de Ciam: columnas Recepción, Impresión, Diseño y Preimpresión con las tarjetas de cada trabajo."
          width={1280}
          height={720}
          loading="eager"
          className="block h-auto w-full"
        />
      </div>
    </div>
  );
}
